import React from 'react';
import find from 'lodash/find';
import { observer } from 'mobx-react-lite';
import moment from 'moment/moment';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useStore } from '../../../../../hooks';
import { IAreaMeterAllReadings } from '../../../../../store/models/metersStore';
import {
  checkDate,
  getConstantName,
  getMeterIcon,
  getPassedDaysStr,
  isArchiveMeter,
  isMultiMeter,
} from '../../../../../utils';
import { INewReadings } from '../../../../../types';
import { Checkbox } from 'components/ui';
import {
  AutomaticLogo,
  CheckboxAbsWrapper,
  CheckboxWrapper,
  CheckDate,
  Icon,
  ListItemSubText,
  ListItemText,
  MultiField,
  StyledTr,
} from './list-item.styled';
import { MeterInput } from '../meter-input';

interface Props {
  meter: IAreaMeterAllReadings;
}

export const ListItem = observer(({ meter }: Props) => {
  const { constants, meters } = useStore();
  const {
    checkedMeters,
    setCheckedMeters,
    metersForPatchReadings,
    setMetersForPatchReadings,
  } = meters;

  const isSelected = !!find(checkedMeters, { id: meter.id });
  const isMulti: boolean = isMultiMeter(meter.type);
  const isDisabledInput: boolean =
    meter.is_automatic ||
    isArchiveMeter(meter.next_check_date, meter.working_finish_date);
  const meterIcon: string = getMeterIcon(meter.type);
  const isElapsed: boolean = checkDate(meter.next_check_date, 3);
  const readingsValues: number[] =
    meter.readings[meter.readings.length - 1]?.values || meter.first_values;

  const lastReadingsPeriod: string =
    meter.readings[meter.readings.length - 1]?.period ||
    meter.installation_date;

  const getCurrentValue = (index: number): string => {
    if (!!find(metersForPatchReadings, { id: meter.id })) {
      return (
        find(metersForPatchReadings, { id: meter.id })?.values[index] || ''
      );
    }

    return String(readingsValues[index]);
  };

  const handleSelect = () => {
    if (isSelected) {
      setCheckedMeters(
        checkedMeters.filter((checkedMeter) => checkedMeter.id !== meter.id)
      );
    } else {
      setCheckedMeters([
        ...checkedMeters,
        {
          id: meter.id,
          values: readingsValues.map(String),
        },
      ]);
    }
  };

  const handleChangeReadings = (index: number) => {
    return function changeReadings(newValue: string) {
      if (!!find(metersForPatchReadings, { id: meter.id })) {
        setMetersForPatchReadings(
          metersForPatchReadings.map((patchMeter: INewReadings) =>
            patchMeter.id === meter.id
              ? {
                  ...patchMeter,
                  values: patchMeter.values.map((val, idx) =>
                    idx === index ? newValue : val
                  ),
                }
              : patchMeter
          )
        );
      } else {
        setMetersForPatchReadings([
          ...metersForPatchReadings,
          {
            id: meter.id,
            values: readingsValues.map((val, idx) =>
              idx === index ? String(newValue) : String(val)
            ),
          },
        ]);
      }
    };
  };

  return (
    <StyledTr isMulti={isMulti}>
      <td>
        <CheckboxWrapper>
          <CheckboxAbsWrapper>
            <Checkbox checked={isSelected} onChange={handleSelect} />
          </CheckboxAbsWrapper>
          <div style={{ overflow: 'hidden' }}>
            <ListItemText>
              <Icon src={meterIcon} />
              <span>
                {getConstantName(
                  constants.requests.MeterTypeNamesShort,
                  meter.type
                )}
              </span>
              {meter.is_automatic && (
                <>
                  <AutomaticLogo
                    data-tooltip-id={`is_automatic_${meter.id}`}
                    data-tooltip-variant="info"
                  />
                  <ReactTooltip
                    id={`is_automatic_${meter.id}`}
                    style={{ zIndex: '10', borderRadius: '8px' }}
                  >
                    <div>
                      Показания счётчика передаются <br />в автоматическом
                      режиме
                    </div>
                  </ReactTooltip>
                </>
              )}
            </ListItemText>
            {meter.serial_number && (
              <ListItemSubText>{meter.serial_number}</ListItemSubText>
            )}
          </div>
        </CheckboxWrapper>
      </td>
      <td>
        <ListItemText>
          {readingsValues.length
            ? getPassedDaysStr(moment(lastReadingsPeriod), moment(), true)
            : 'Отсутствуют'}
        </ListItemText>
        <ListItemSubText>
          {readingsValues.length
            ? moment(lastReadingsPeriod).format('DD.MM.YYYY')
            : 'Отсутствует'}
        </ListItemSubText>
      </td>
      <td>
        {isMulti && (
          <MultiField>
            <span>т1</span>
            <span>т2</span>
            {meter.type === 'ElectricThreeRateAreaMeter' && <span>т3</span>}
          </MultiField>
        )}
      </td>
      <td>
        <MultiField>
          {readingsValues.map((_, index) => (
            <MeterInput
              key={meter.id + index}
              isDisabled={isDisabledInput}
              currentValue={getCurrentValue(index)}
              initialValue={readingsValues[index]}
              meterType={meter.type}
              changeReading={handleChangeReadings(index)}
              isReadingsChanged={meters.isMeterReadingsChanges(meter.id, index)}
            />
          ))}
        </MultiField>
      </td>
      <td style={{ textAlign: 'center' }}>
        <CheckDate>
          <ListItemText isElapsed={isElapsed}>
            {getPassedDaysStr(moment(meter.next_check_date), moment(), true)}
          </ListItemText>
          <ListItemSubText>
            {moment(meter.next_check_date).format('DD.MM.YYYY')}
          </ListItemSubText>
        </CheckDate>
      </td>
    </StyledTr>
  );
});
